/**
 * Live updates: one WebSocket per tab to /api/v1/live.
 *
 * Events only say *which machine changed and what kind of data*; the affected queries are then
 * refetched from the REST API, which stays the single source of truth. A lost event therefore
 * only delays a refresh (pages also poll slowly), and live updates can never create duplicate
 * or inconsistent records in the UI.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { getAccessToken, getActingCompany } from "../api/client";

export type LiveStatus = "connecting" | "live" | "offline";

type LiveMessage =
  | { type: "ready" }
  | { type: "resync" }
  | { type: "event"; machine_id: string; factory_id: string; kinds: string[] };

/** Query keys refreshed per event kind (machine-specific keys get the machine id appended). */
const MACHINE_KEYS: Record<string, string[]> = {
  telemetry: ["latest"],
  state: ["machine-state", "states", "downtime"],
  production: ["machine-state", "production-events"],
  downtime: ["machine-state", "downtime"],
  diagnostic: ["diagnostics"],
};

export function liveUrl(origin: string): string {
  return `${origin.replace(/^http/, "ws")}/api/v1/live`;
}

export function useLiveUpdates(enabled: boolean): LiveStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>("connecting");
  const pending = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let ws: WebSocket | null = null;
    let stopped = false;
    let retry = 1000;
    let reconnect: ReturnType<typeof setTimeout> | null = null;

    // Coalesce bursts (a gateway sends several messages per second) into one refresh.
    const flush = () => {
      timer.current = null;
      const keys = [...pending.current];
      pending.current.clear();
      for (const k of keys) {
        if (k === "*") void qc.invalidateQueries();
        else void qc.invalidateQueries({ queryKey: JSON.parse(k) as unknown[] });
      }
    };
    const schedule = (key: unknown[] | "*") => {
      pending.current.add(key === "*" ? "*" : JSON.stringify(key));
      timer.current ??= setTimeout(flush, 500);
    };

    const connect = () => {
      const token = getAccessToken();
      if (!token) {
        reconnect = setTimeout(connect, 2000);
        return;
      }
      setStatus("connecting");
      ws = new WebSocket(liveUrl(window.location.origin));
      ws.onopen = () => {
        const company = getActingCompany();
        ws?.send(JSON.stringify({ type: "auth", token, ...(company ? { company_id: company } : {}) }));
      };
      ws.onmessage = (e) => {
        const msg = JSON.parse(String(e.data)) as LiveMessage;
        if (msg.type === "ready") {
          retry = 1000;
          setStatus("live");
          schedule("*"); // anything may have changed while we were not connected
        } else if (msg.type === "resync") {
          schedule("*");
        } else if (msg.type === "event") {
          schedule(["dashboard"]);
          for (const kind of msg.kinds)
            for (const key of MACHINE_KEYS[kind] ?? []) schedule([key, msg.machine_id]);
        }
      };
      ws.onclose = () => {
        setStatus("offline");
        if (!stopped) {
          reconnect = setTimeout(connect, retry);
          retry = Math.min(retry * 2, 30_000);
        }
      };
    };
    connect();
    return () => {
      stopped = true;
      if (reconnect) clearTimeout(reconnect);
      if (timer.current) clearTimeout(timer.current);
      ws?.close();
    };
  }, [enabled, qc]);

  return enabled ? status : "offline";
}
